// ---------------------------------------------------------------------------
// NEWEST SAVED VERSION OF A GOAL
// ---------------------------------------------------------------------------
// A goal's saved values can live in two kinds of pathway_goals row:
//
//   * the PLANNING goals   - written by insert_pathway_goals.php (the
//     Intervention Planning "Update" screen). One row per goal, replaced
//     wholesale whenever that screen is saved again.
//   * the REVIEW snapshots - written as goal_type = 'review' rows linked to a
//     pathway_reviews row, one immutable snapshot per update. /goalreview and
//     the Review Smart Goal revision BOTH append one of these.
//
// So the newest statement of a goal is whichever of the two was written last,
// and every page that shows "the goal" has to resolve that rather than reading
// the planning rows blindly. Reading only the planning rows made
// /updategoal and /goalsetdownload keep showing the goal as it was FIRST set,
// because a revision never touches those rows any more.
//
// This mirrors, on the client, the same comparison get_goals_page.php performs
// server side, so the Review Existing Goals table and these pages always agree
// on which version is current.
//
// get_test_intervention.php returns:
//
//   {
//     goals:        [ ...planning goal rows... ],
//     review_goals: [ { review_id, review_no, review_created_at,
//                      goals: [ ...snapshot rows... ] }, ... ]   // oldest first
//   }
// ---------------------------------------------------------------------------

const versionStamp = (value) => {
  const t = Date.parse(value || "");
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Newest timestamp written across a set of goal rows.
 */
const stampOfGoals = (goals) =>
  (Array.isArray(goals) ? goals : []).reduce(
    (max, g) =>
      Math.max(
        max,
        versionStamp(g?.updated_at),
        versionStamp(g?.created_at)
      ),
    0
  );

/**
 * Pick the goals that represent the goal's CURRENT saved state.
 *
 * @param {Array} planningGoals payload.goals
 * @param {Array} reviewGroups  payload.review_goals
 * @returns {Array} the planning goals, or the latest snapshot's goals when that
 *                   snapshot is the more recent write.
 */
export const newestVersionGoals = (planningGoals, reviewGroups) => {
  const planning = Array.isArray(planningGoals) ? planningGoals : [];
  const reviews = Array.isArray(reviewGroups) ? reviewGroups : [];
  const latestReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;

  const planningNewest = stampOfGoals(planning);
  const reviewNewest = latestReview
    ? Math.max(
        stampOfGoals(latestReview.goals),
        versionStamp(latestReview.review_created_at)
      )
    : 0;

  if (planningNewest >= reviewNewest) return planning;
  return latestReview && Array.isArray(latestReview.goals) && latestReview.goals.length > 0
    ? latestReview.goals
    : planning;
};

/**
 * Same resolution, applied to a whole get_test_intervention.php payload.
 *
 * Returns a shallow copy with `goals` replaced, so a caller can keep using
 * every other key (`data`, `session`, ...) exactly as the API sent it and only
 * the goal rows are re-pointed at the newest version. The original payload is
 * never mutated.
 *
 * @param {object} payload parsed get_test_intervention.php response
 */
export const withNewestGoalVersion = (payload) => {
  if (!payload || typeof payload !== "object") return payload;

  const planning = Array.isArray(payload.goals) ? payload.goals : [];
  const effective = newestVersionGoals(planning, payload.review_goals);

  // Same array identity means nothing changed - return the payload untouched so
  // callers can use the result as a stable dependency.
  if (effective === planning) return payload;

  return { ...payload, goals: effective };
};

export default newestVersionGoals;